/**
 * Built-in validation helper functions for EntityForm.
 *
 * Provides synchronous field/form validation against Zod schemas,
 * debounced async validation, and cross-field validation execution.
 */

import { z } from 'zod';

import type { CrossFieldRule, FieldValidationConfig } from '../types.ts';
import type { CrossFieldValidator } from './registry.ts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The shape returned by async validation config. */
export interface AsyncValidationConfig {
  /** ERP endpoint to call for validation */
  erpEndpoint: string;
  /** Debounce delay in ms before calling the endpoint */
  debounceMs?: number;
  /** Error message key if validation fails */
  message?: string;
}

/** A map of field names to error messages. */
export type ErrorMap = Record<string, string | undefined>;

// ---------------------------------------------------------------------------
// Synchronous validation
// ---------------------------------------------------------------------------

/**
 * Validate a single field value against its portion of the Zod schema.
 *
 * Extracts the field's sub-schema from the object schema and runs
 * `safeParse`. Returns the first error message on failure, or
 * `undefined` on success.
 *
 * @param fieldName - The field name to validate.
 * @param value     - The current field value.
 * @param schema    - The full form Zod object schema.
 * @returns An error message string or `undefined` if valid.
 */
export function validateFieldSync(
  fieldName: string,
  value: unknown,
  schema: z.ZodObject<Record<string, z.ZodType>>,
): string | undefined {
  const fieldSchema = schema.shape[fieldName] as z.ZodType | undefined;

  // If no schema exists for this field (e.g. hidden), it passes.
  if (!fieldSchema) {
    return undefined;
  }

  const result = fieldSchema.safeParse(value);

  if (result.success) {
    return undefined;
  }

  // Return the first issue's message.
  const firstIssue = result.error.issues[0];
  return firstIssue?.message ?? 'Validation failed';
}

/**
 * Validate all form values against the full Zod object schema.
 *
 * Runs `safeParse` on the entire values object and returns a map of
 * field names to their first error message. Fields without errors
 * are set to `undefined`.
 *
 * @param values - All current form values.
 * @param schema - The full form Zod object schema.
 * @returns A map of field names to error messages.
 */
export function validateFormSync(
  values: Record<string, unknown>,
  schema: z.ZodObject<Record<string, z.ZodType>>,
): ErrorMap {
  const result = schema.safeParse(values);

  if (result.success) {
    return {};
  }

  const errors: ErrorMap = {};

  for (const issue of result.error.issues) {
    // Path[0] is the field name for a flat object schema.
    const fieldName = issue.path[0];

    if (typeof fieldName === 'string' && errors[fieldName] === undefined) {
      // Only record the first error per field.
      errors[fieldName] = issue.message;
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Async validation
// ---------------------------------------------------------------------------

/**
 * Create a debounced async validator function from an async validation config.
 *
 * The returned function accepts a field value and calls the configured ERP
 * endpoint after the debounce period. If the value changes before the
 * debounce completes, the previous call is cancelled.
 *
 * @param asyncConfig - The async validation configuration from FieldValidationConfig.
 * @returns A function that accepts a value and returns a Promise resolving
 *          to an error message or `undefined`.
 */
export function createAsyncValidator(
  asyncConfig: NonNullable<FieldValidationConfig['async']>,
): (value: unknown) => Promise<string | undefined> {
  const debounceMs = asyncConfig.debounceMs ?? 400;

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let abortController: AbortController | null = null;

  return (value: unknown): Promise<string | undefined> => {
    // Cancel any pending debounce or in-flight request.
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    // Skip async validation for empty values -- let required validation handle that.
    if (value === '' || value === null || value === undefined) {
      return Promise.resolve(undefined);
    }

    return new Promise<string | undefined>((resolve) => {
      timeoutId = setTimeout(() => {
        timeoutId = null;
        abortController = new AbortController();
        const { signal: _signal } = abortController;

        // The actual ERP call would go here. For now we expose the endpoint
        // info and provide a placeholder that modules can integrate with
        // their own ERP client. This function is designed to be wrapped
        // by the hook layer which has access to the ERP client.
        //
        // Expected API contract:
        //   POST asyncConfig.erpEndpoint
        //   Body: { value }
        //   Response: { valid: boolean, message?: string }
        //
        // Since we don't have direct access to the ERP client at this layer,
        // we resolve immediately. The hook layer (useFormValidation) should
        // replace this with a real implementation.
        // Placeholder: resolve immediately. The hook layer (useFormValidation)
        // replaces this with a real ERP call using `signal`, `asyncConfig.message`,
        // and `asyncConfig.erpEndpoint`.
        resolve(undefined);
      }, debounceMs);
    });
  };
}

// ---------------------------------------------------------------------------
// Cross-field validation
// ---------------------------------------------------------------------------

/** A failing cross-field rule: the field its error is attached to, and the message. */
export interface CrossFieldError {
  field: string;
  message: string;
}

/**
 * Run one cross-field rule against the given values.
 *
 * The rule's validator is looked up in `registry` and invoked with the
 * values and the rule definition. The rule-level `message` overrides the
 * validator's message.
 *
 * @param values   - The values handed to the validator.
 * @param rule     - The cross-field rule from config.
 * @param registry - Map of validator keys to CrossFieldValidator functions.
 * @returns The error to show, or `null` when the rule passes or its
 *          validator is not registered (skipped, with a warning in DEV).
 */
export function evaluateCrossFieldRule(
  values: Record<string, unknown>,
  rule: CrossFieldRule,
  registry: Map<string, CrossFieldValidator>,
): CrossFieldError | null {
  const validator = registry.get(rule.validator);

  if (!validator) {
    // Validator not registered; skip silently in production.
    /* v8 ignore start -- false branch unreachable in test env (DEV is always true) */
    if (import.meta.env.DEV) {
      /* v8 ignore stop */
      console.warn(
        `[EntityForm] Cross-field validator "${rule.validator}" is not registered.`,
      );
    }
    return null;
  }

  const result = validator(values, rule);

  if (result === null) {
    return null;
  }

  // Use the rule-level message override if provided, otherwise use the
  // message from the validator result.
  return { field: result.field, message: rule.message ?? result.message };
}

/**
 * Run all cross-field validation rules against the current form values.
 *
 * Each rule references a registered CrossFieldValidator by key. The
 * validator is looked up in the registry and invoked with the current
 * values and the rule definition.
 *
 * @param values   - All current form values.
 * @param rules    - Array of cross-field validation rules from config.
 * @param registry - Map of validator keys to CrossFieldValidator functions.
 * @returns A map of field names to error messages from failing validators.
 */
export function runCrossFieldValidation(
  values: Record<string, unknown>,
  rules: CrossFieldRule[],
  registry: Map<string, CrossFieldValidator>,
): ErrorMap {
  const errors: ErrorMap = {};

  for (const rule of rules) {
    const error = evaluateCrossFieldRule(values, rule, registry);

    if (error !== null) {
      // Only set the error if one isn't already present for this field.
      errors[error.field] ??= error.message;
    }
  }

  return errors;
}
